import React, {
  createRef,
  useRef,
  useEffect,
  useState,
  useCallback,
  KeyboardEvent,
} from 'react';
import { InputContainer } from '@highlight-ui/input';
import { suffixMetadata, noop } from '@highlight-ui/utils-commons';
import classnames from 'classnames';
import { Icon, InteractiveIcon } from '@highlight-ui/icon';
import styles from './MultiInputTrigger.module.scss';

import { SelectChip } from '../SelectChip';
import { SelectOption, SelectProps } from '../types';
import useResizeObserver from './useResizeObserver';
import { PopoverTriggerProps } from '../Popover';
import { calculateChipsShownNumber } from './helpers';

export type MultiInputTriggerProps<T extends SelectOption> = Partial<
  Pick<
    PopoverTriggerProps<T>,
    | 'disabled'
    | 'label'
    | 'triggerId'
    | 'outline'
    | 'autoFocus'
    | 'visible'
    | 'onClick'
    | 'onClear'
    | 'selectedOptions'
    | 'onRemove'
    | 'onKeyDown'
  >
> &
  Pick<SelectProps<T>, 'limitTwoRows'>;

const MultiInputTrigger = React.forwardRef(
  <T extends SelectOption>(
    {
      disabled,
      label,
      outline = 'default',
      autoFocus = false,
      triggerId,
      visible = false,
      onClick = noop,
      onKeyDown = noop,
      onClear,
      onRemove,
      limitTwoRows = true,
      selectedOptions = [],
    }: MultiInputTriggerProps<T>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const [elRefs, setElRefs] = useState<React.RefObject<HTMLDivElement>[]>([]);
    const chipContainerRef = useRef<HTMLDivElement>(null);
    const numberChipRef = useRef<HTMLDivElement>(null);
    const [isVisibleInput, setIsVisibleInput] = useState(visible);
    const [showAllRows, setShowAllRows] = useState(limitTwoRows);
    const [disableTabChip, setDisableTabChip] = useState(false);
    const { dimensions } = useResizeObserver();

    const [chipsShownNumber, setChipsShownNumber] = useState(0);

    const hasSelectedOption = selectedOptions.length > 0;

    useEffect(() => {
      setIsVisibleInput(visible);
      setShowAllRows(!limitTwoRows);
    }, [limitTwoRows, visible, disabled]);

    useEffect(() => {
      if (autoFocus && chipContainerRef.current) {
        chipContainerRef.current.focus();
      }
    }, [autoFocus]);

    useEffect(() => {
      setElRefs((prevState) =>
        Array(selectedOptions.length)
          .fill(0)
          .map((_, i) => prevState[i] || createRef()),
      );
    }, [selectedOptions]);

    useEffect(() => {
      setChipsShownNumber(
        calculateChipsShownNumber(
          elRefs,
          chipContainerRef,
          isVisibleInput && !showAllRows,
        ),
      );
    }, [
      isVisibleInput,
      elRefs,
      chipContainerRef,
      dimensions.width,
      showAllRows,
      disabled,
    ]);

    const expandInputTrigger = () => {
      setIsVisibleInput(true);
      setShowAllRows(true);
    };

    const hideChips = useCallback(
      (index: number) =>
        (!showAllRows || !isVisibleInput) && index >= chipsShownNumber,
      [chipsShownNumber, isVisibleInput, showAllRows],
    );

    const showChipsNum = useCallback(
      (index: number) =>
        (!showAllRows || !isVisibleInput) &&
        index === chipsShownNumber - 1 &&
        selectedOptions.length > chipsShownNumber,
      [selectedOptions.length, chipsShownNumber, isVisibleInput, showAllRows],
    );

    const focusChipElement = useCallback(
      (index: number) => {
        if (elRefs.length > 0 && elRefs[index].current) {
          elRefs[index].current?.focus();
          setDisableTabChip(true);
        }
      },
      [elRefs],
    );

    const onClickClearAll = useCallback(
      (e) => {
        e.stopPropagation();
        if (onClear) {
          onClear(e);
        }
        setDisableTabChip(false);
        setTimeout(() => {
          chipContainerRef.current?.focus();
        }, 0);
      },
      [onClear],
    );

    const handleKeyboardClearAll = (e: KeyboardEvent<HTMLSpanElement>) => {
      if ([' ', 'Enter'].includes(e.key)) onClickClearAll(e);
    };

    const handleContainerFocus = useCallback(
      (e) => {
        if (
          !disableTabChip &&
          e.target === e.currentTarget &&
          elRefs.length > 0 &&
          elRefs[0].current
        ) {
          setDisableTabChip(true);
          elRefs[0].current.focus();
        }
      },
      [disableTabChip, elRefs],
    );

    const handleContainerBlur = useCallback((e) => {
      if (e.target !== e.currentTarget) {
        setDisableTabChip(false);
      }
    }, []);

    const handleChipNavigation = useCallback(
      (index: number) => (event: React.KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
          case 'ArrowRight':
            event.preventDefault();
            if (index >= elRefs.length - 1) {
              focusChipElement(0);
              break;
            }

            if (
              selectedOptions.length !== chipsShownNumber &&
              index + 1 >= chipsShownNumber
            ) {
              if (!showAllRows) {
                numberChipRef.current?.focus();
                setDisableTabChip(true);
                break;
              }
              expandInputTrigger();
            }
            setTimeout(() => focusChipElement(index + 1), 0);
            break;

          case 'ArrowLeft':
            event.preventDefault();
            if (index > 0) {
              focusChipElement(index - 1);
              break;
            }

            if (!showAllRows) {
              numberChipRef.current?.focus();
              setDisableTabChip(true);
              break;
            }

            if (selectedOptions.length !== chipsShownNumber) {
              expandInputTrigger();
            }
            setTimeout(() => focusChipElement(elRefs.length - 1), 0);
            break;

          default:
            break;
        }
      },
      [
        chipsShownNumber,
        elRefs.length,
        focusChipElement,
        selectedOptions.length,
        showAllRows,
      ],
    );

    const handleChipRemoval = useCallback(
      (index: number, option: T) =>
        (
          e:
            | React.KeyboardEvent<HTMLDivElement>
            | React.MouseEvent<HTMLSpanElement, MouseEvent>,
        ) => {
          if (!onRemove) return;
          e.stopPropagation();
          if (elRefs.length > 1) {
            (elRefs[index + 1]?.current || elRefs[index - 1]?.current)?.focus();
            setDisableTabChip(true);
          } else {
            chipContainerRef.current?.focus();
          }
          onRemove(option);
        },
      [elRefs, onRemove],
    );

    const handleNumberChipSelection = useCallback((e) => {
      e.stopPropagation();
      expandInputTrigger();
    }, []);

    const handleNumberChipNavigation = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
          case 'ArrowRight':
            event.preventDefault();
            focusChipElement(0);
            break;

          case 'ArrowLeft':
            event.preventDefault();
            focusChipElement(chipsShownNumber - 1);
            break;

          case 'Enter':
            event.preventDefault();
            handleNumberChipSelection(event);
            setTimeout(() => {
              focusChipElement(chipsShownNumber);
            }, 0);
            break;

          default:
            break;
        }
      },
      [chipsShownNumber, focusChipElement, handleNumberChipSelection],
    );

    return (
      <InputContainer
        containerRef={ref}
        outline={outline}
        onOutlineClick={onClick}
        className={classnames(styles.selectTrigger, {
          [styles.limitHeight]: !showAllRows,
          [styles.disabled]: disabled,
          [styles.focusedOutline]: !visible && !hasSelectedOption,
        })}
        inputClassName={classnames(styles.chipsContainer, {
          [styles.selected]: hasSelectedOption,
          [styles.focused]: isVisibleInput && hasSelectedOption,
        })}
        disabled={disabled}
        suffix={
          <div className={styles.selectTriggerIcons}>
            {onClear && hasSelectedOption ? (
              <InteractiveIcon
                iconColor="icon-subdued"
                disabled={disabled}
                iconName="times-circle"
                iconType="solid"
                aria-label="Clear Select Input"
                onClick={onClickClearAll}
                onKeyDown={handleKeyboardClearAll}
                onFocus={() => setDisableTabChip(false)}
              />
            ) : null}

            <Icon
              type="solid"
              name={visible ? 'angle-up' : 'angle-down'}
              aria-label="Toggle visibility"
              className={styles.icon}
              color={disabled ? 'icon-disabled' : 'icon-default'}
            />
          </div>
        }
      >
        <div
          ref={chipContainerRef}
          id={triggerId}
          role="button"
          onKeyDown={onKeyDown}
          onFocus={handleContainerFocus}
          onBlur={handleContainerBlur}
          tabIndex={disableTabChip ? -1 : 0}
        >
          {selectedOptions.map((option, index) => (
            <React.Fragment key={option.value}>
              <SelectChip
                ref={elRefs[index]}
                metadata={suffixMetadata(option.metadata, `tag-${index}`)}
                className={classnames({
                  [styles.isHidden]: hideChips(index),
                })}
                onKeyDown={handleChipNavigation(index)}
                onRemove={handleChipRemoval(index, option)}
                disabled={disabled || option?.disabled}
                tabIndex={-1}
              >
                {option.label}
              </SelectChip>

              {showChipsNum(index) && (
                <SelectChip
                  ref={numberChipRef}
                  className={classnames({
                    [styles.chipSelected]: isVisibleInput,
                  })}
                  onKeyDown={handleNumberChipNavigation}
                  onClick={handleNumberChipSelection}
                  disabled={disabled || option?.disabled}
                  tabIndex={-1}
                >{`+${selectedOptions.length - chipsShownNumber}`}</SelectChip>
              )}
            </React.Fragment>
          ))}

          {selectedOptions.length === 0 && (
            <span className={styles.labelText}>{label}</span>
          )}
        </div>
      </InputContainer>
    );
  },
);

export default MultiInputTrigger;
